/**
 * V2 customer workspace for MSSP-managed, customer-managed, and shared services and their links to
 * existing WhiteHaX endpoints, profiles, schedules, JOBs, and SCAN_RESULT rows.
 */
import { ArrowLeft, Boxes, CalendarClock, FileText, Save, Send, Users, Workflow } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { generateServiceReport, getCustomer, saveCustomerService } from "./msspApi";

const tabs = ["overview", "users", "services", "test activity", "reports"];
const formatDate = (value) => value ? new Date(value).toLocaleString() : "Not scheduled";
const display = (value) => String(value || "—").replaceAll("_", " ");

export default function MsspCustomerDetail() {
  const { licenseId } = useParams();
  const [customer, setCustomer] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [selectedServiceId, setSelectedServiceId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // The backend must resolve licenseId to the dedicated MSSP instance; route
    // parameters are identifiers, never authorization decisions.
    getCustomer(licenseId).then((data) => {
      setCustomer(data);
      const first = data.services?.[0];
      setSelectedServiceId(first?.id || null);
      setDraft(first ? structuredClone(first) : null);
    }).catch((err) => setError(err.message));
  }, [licenseId]);

  const selectedService = useMemo(
    () => customer?.services?.find((service) => service.id === selectedServiceId) || null,
    [customer, selectedServiceId]
  );

  const selectService = (service) => {
    setSelectedServiceId(service.id);
    setDraft(structuredClone(service));
    setNotice(""); setError("");
  };

  const update = (field, value) => setDraft((current) => ({ ...current, [field]: value }));

  const save = async () => {
    setBusy(true); setError(""); setNotice("");
    try {
      const saved = await saveCustomerService(selectedServiceId, {
        managementMode: draft.managementMode,
        status: draft.status,
        summaryReportFrequency: draft.summaryReportFrequency,
        summaryReportDay: draft.summaryReportDay,
        reportRecipients: draft.reportRecipients,
        reportPolicy: draft.reportPolicy,
        serviceConfig: draft.serviceConfig,
      });
      setCustomer((current) => ({ ...current, services: current.services.map((item) => item.id === saved.id ? saved : item) }));
      setDraft(structuredClone(saved));
      setNotice("Customer service saved in mock mode.");
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  const generate = async () => {
    if (!selectedServiceId) return;
    setBusy(true); setError(""); setNotice("");
    try {
      const run = await generateServiceReport(selectedServiceId);
      setNotice(`Aggregated service report ${run.reportRunId} queued for ${selectedService.definition.name}.`);
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  if (error && !customer) return <section className="mssp-page"><div className="mssp-alert error">{error}</div></section>;
  if (!customer) return <section className="mssp-page"><div className="mssp-empty">Loading customer…</div></section>;

  return (
    <section className="mssp-page">
      <Link className="mssp-back" to="/mssp/customers"><ArrowLeft size={16} /> All customers</Link>
      <div className="mssp-page-heading">
        <div><p className="mssp-eyebrow">License #{customer.licenseId}</p><h1>{customer.companyName}</h1><p>{customer.adminName} · {customer.adminEmail}</p></div>
        <button className="mssp-button primary" onClick={generate} disabled={busy || !selectedServiceId}><Send size={16} /> Generate service report</button>
      </div>

      {(notice || error) && <div className={`mssp-alert ${error ? "error" : "success"}`} role="status">{error || notice}</div>}
      <div className="mssp-tabs" role="tablist" aria-label="Customer details">
        {tabs.map((tab) => <button key={tab} role="tab" aria-selected={activeTab === tab} onClick={() => setActiveTab(tab)}>{tab}</button>)}
      </div>

      {activeTab === "overview" && <div className="mssp-detail-grid">
        <div className="mssp-panel mssp-detail-card"><h2>License</h2><dl><div><dt>Status</dt><dd>{customer.licenseActive ? "Active" : "Expired"}</dd></div><div><dt>Key</dt><dd>{customer.licenseKey}</dd></div><div><dt>Valid through</dt><dd>{new Date(customer.endDate).toLocaleDateString()}</dd></div><div><dt>Seats</dt><dd>{customer.activeUsers} of {customer.maxUsers}</dd></div></dl></div>
        <div className="mssp-panel mssp-detail-card"><h2>Service coverage</h2><dl><div><dt>Subscriptions</dt><dd>{customer.services.length}</dd></div><div><dt>MSSP-managed</dt><dd>{customer.services.filter((item) => item.managementMode === "mssp_managed").length}</dd></div><div><dt>Customer-managed</dt><dd>{customer.services.filter((item) => item.managementMode === "customer_managed").length}</dd></div><div><dt>Shared</dt><dd>{customer.services.filter((item) => item.managementMode === "shared").length}</dd></div><div><dt>Next summary</dt><dd>{formatDate(customer.nextReportAt)}</dd></div></dl></div>
        <div className="mssp-panel mssp-detail-card"><h2>Current posture</h2><div className="mssp-score"><strong>{customer.latestScore}%</strong><span>Latest readiness score</span></div><p>{customer.openFindings} open findings require review.</p></div>
      </div>}

      {activeTab === "users" && <DataTable title="Customer users" icon={<Users size={18} />} count={customer.users.length} headers={["Name", "Email", "Role", "Last login"]} rows={customer.users.map((user) => [user.name, user.email, display(user.role), formatDate(user.lastLogin)])} />}

      {activeTab === "services" && <div className="mssp-service-layout">
        <aside className="mssp-panel mssp-service-list"><div className="mssp-panel-heading"><h2><Boxes size={18} /> Subscribed services</h2></div>{customer.services.map((service) => <button key={service.id} className={service.id === selectedServiceId ? "active" : ""} onClick={() => selectService(service)}><strong>{service.definition.name}</strong><span>{display(service.managementMode)} · {display(service.status)}</span></button>)}{customer.services.length === 0 && <div className="mssp-empty">No services subscribed.</div>}</aside>
        {draft && <div className="mssp-panel mssp-form-panel">
          <div className="mssp-panel-heading"><div><h2>{draft.definition.name}</h2><p>{draft.definition.description}</p></div><button className="mssp-button primary" onClick={save} disabled={busy}><Save size={16} /> Save</button></div>
          <div className="mssp-form-grid">
            <label>Management mode<select value={draft.managementMode} onChange={(event) => update("managementMode", event.target.value)}>{draft.definition.allowedManagementModes.map((mode) => <option key={mode} value={mode}>{display(mode)}</option>)}</select></label>
            <label>Status<select value={draft.status} onChange={(event) => update("status", event.target.value)}>{["draft", "active", "suspended", "expired", "cancelled"].map((value) => <option key={value}>{value}</option>)}</select></label>
            <label>Summary report frequency<select value={draft.summaryReportFrequency} onChange={(event) => update("summaryReportFrequency", event.target.value)}>{["none", "weekly", "monthly", "quarterly"].map((value) => <option key={value}>{value}</option>)}</select></label>
            <label>Summary report day<input type="number" min="1" max="28" value={draft.summaryReportDay} onChange={(event) => update("summaryReportDay", Number(event.target.value))} /></label>
            <label className="mssp-field-wide">Recipients<textarea rows="2" value={draft.reportRecipients.join(", ")} onChange={(event) => update("reportRecipients", event.target.value.split(",").map((item) => item.trim()).filter(Boolean))} /><small>TODO_BACKEND_API: validate addresses and membership before saving.</small></label>
          </div>
          <div className="mssp-linked-grid">
            <LinkedList title="Assigned responsibilities" items={draft.roles.map((role) => `${role.name || `User ${role.userId}`} — ${display(role.responsibility)}`)} />
            <LinkedList title="Linked profiles" items={draft.profileLinks.map((link) => `Profile ${link.profileId} — ${link.purpose}`)} />
            <LinkedList title="Linked schedules" items={draft.scheduleLinks.map((link) => `Schedule ${link.jobScheduleId} — ${link.timezone} / ${display(link.approvalPolicy)}`)} />
          </div>
        </div>}
      </div>}

      {activeTab === "test activity" && <div className="mssp-stack">
        <DataTable title="Endpoint and profile inventory" icon={<Workflow size={18} />} count={customer.profiles.length} headers={["Profile", "Type", "Endpoint"]} rows={customer.profiles.map((profile) => [profile.name, display(profile.profileType), customer.endpoints.find((endpoint) => endpoint.id === profile.endpointId)?.name || profile.endpointId])} />
        <DataTable title="WhiteHaX JOB execution history" icon={<CalendarClock size={18} />} count={customer.testRuns.length} headers={["JOB", "Service", "Profile", "Status", "Created", "SCAN_RESULT", "Readiness"]} rows={customer.testRuns.map((run) => [`#${run.jobId}`, customer.services.find((service) => service.id === run.customerServiceId)?.definition.name || "Unlinked", run.profileName, run.status, formatDate(run.createdAt), run.scanResultId || "—", run.readiness == null ? "—" : `${run.readiness}%`])} />
      </div>}

      {activeTab === "reports" && <DataTable title="Existing SCAN_RESULT reports" icon={<FileText size={18} />} count={customer.reports.length} headers={["ID", "Type", "Name", "Created", "Score", "Status"]} rows={customer.reports.map((report) => [report.id, display(report.type), report.name, formatDate(report.createdAt), `${report.score}%`, report.status])} />}
    </section>
  );
}

function DataTable({ title, icon, count, headers, rows }) {
  return <div className="mssp-panel"><div className="mssp-panel-heading"><h2>{icon}{title}</h2><span>{count} shown</span></div><div className="mssp-table-wrap"><table className="mssp-table"><thead><tr>{headers.map((header) => <th key={header}>{header}</th>)}</tr></thead><tbody>{rows.map((row, index) => <tr key={index}>{row.map((value, cell) => <td key={cell}>{cell === 0 ? <strong>{value}</strong> : value}</td>)}</tr>)}</tbody></table></div></div>;
}

function LinkedList({ title, items }) {
  return <section><h3>{title}</h3>{items.length ? <ul>{items.map((item) => <li key={item}>{item}</li>)}</ul> : <p>No links configured.</p>}</section>;
}
